import React from 'react';
import {
  AllowAudioPlaybackProps,
  isTrackReference,
  LiveKitRoom,
  LiveKitRoomProps,
  RoomAudioRenderer,
  RoomAudioRendererProps,
  StartAudio,
  VideoTrack,
  VideoTrackProps,
  useTracks,
} from '@livekit/components-react';
import {Track} from 'livekit-client';
import {LiveSessionSummary} from '../../../../services/liveSessionsApi';
import HomeModalShell from './HomeModalShell';
import LiveSessionIdentity from './LiveSessionIdentity';
import styles from '../Home.module.css';

const LiveKitRoomComponent = LiveKitRoom as React.ComponentType<LiveKitRoomProps>;
const RoomAudioRendererComponent = RoomAudioRenderer as React.ComponentType<RoomAudioRendererProps>;
const StartAudioComponent = StartAudio as React.ComponentType<AllowAudioPlaybackProps>;
const VideoTrackComponent = VideoTrack as React.ComponentType<VideoTrackProps>;

interface LiveViewerModalProps {
  isOpen: boolean;
  session: LiveSessionSummary | null;
  accessToken: string | null;
  liveKitUrl: string | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onError: (error: Error) => void;
}

const ViewerVideoSurface: React.FC = () => {
  const trackReferences = useTracks([{source: Track.Source.Camera, withPlaceholder: true}]);
  const remoteCameraTrack = trackReferences.find((trackReference) => !trackReference.participant.isLocal);

  return (
    <div className={styles.liveModalVideoShell}>
      {remoteCameraTrack && isTrackReference(remoteCameraTrack) ? (
        <VideoTrackComponent
          className={styles.liveModalVideo}
          trackRef={remoteCameraTrack}
        />
      ) : (
        <div className={styles.liveModalVideoPlaceholder}>
          Waiting for the broadcaster&apos;s camera feed...
        </div>
      )}
      <RoomAudioRendererComponent />
      <div className={styles.liveViewerAudioPrompt}>
        <StartAudioComponent label="Enable audio" />
      </div>
    </div>
  );
};

const LiveViewerModal: React.FC<LiveViewerModalProps> = ({
  isOpen,
  session,
  accessToken,
  liveKitUrl,
  loading,
  error,
  onClose,
  onConnected,
  onDisconnected,
  onError,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.modalBackdrop}
      panelClassName={`${styles.modalCard} ${styles.liveModalCard}`.trim()}
      ariaLabelledBy="live-viewer-modal-title"
      ariaDescribedBy="live-viewer-modal-description"
      onClose={onClose}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="live-viewer-modal-title" className={styles.modalTitle}>
            {session?.title || 'Watch live'}
          </h2>
          <p id="live-viewer-modal-description" className={styles.modalDescription}>
            {session
              ? `Streaming from ${session.broadcaster.name}.`
              : 'Joining the live session.'}
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close live viewer modal"
        >
          ×
        </button>
      </div>

      {session ? (
        <div className={styles.liveModalSessionSummary}>
          <LiveSessionIdentity broadcaster={session.broadcaster} label={session.broadcaster.name} />
          <span className={styles.liveModalSessionMeta}>{session.viewerCount} watching</span>
        </div>
      ) : null}

      {loading ? (
        <div className={styles.liveModalVideoShell}>
          <div className={styles.liveModalVideoPlaceholder}>Joining live session...</div>
        </div>
      ) : null}

      {!loading && accessToken && liveKitUrl ? (
        <LiveKitRoomComponent
          token={accessToken}
          serverUrl={liveKitUrl}
          connect={true}
          audio={false}
          video={false}
          onConnected={onConnected}
          onDisconnected={() => {
            onDisconnected();
          }}
          onError={onError}
        >
          <ViewerVideoSurface />
        </LiveKitRoomComponent>
      ) : null}

      {error ? <p className={styles.liveModalError}>{error}</p> : null}
    </HomeModalShell>
  );
};

export default React.memo(LiveViewerModal);
